// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * Core type definitions for the Asset Management system.
 * This module defines the foundational data structures and validation schemas used
 * throughout the asset management interface, including asset ownership, events,
 * files, and accounting properties.
 */
import { exceptionTypeSchema } from '@/constants/asset-exception-type';
import { assetFunctionalStatusSchema } from '@/constants/asset-functional-status';
import {
  costAdjustmentReasonSchema,
  costAdjustmentTypeSchema,
} from '@/constants/cost-adjustment';
import { orgDefaultCurrencyCodeSchema } from '@/constants/currency';
import { depreciationModelSchema } from '@/constants/depreciation-model';
import { shippingCarrierSchema } from '@/constants/shipping-carrier';
import { usefulLifeRevisionReasonSchema } from '@/constants/useful-life-revision';
import { currencySchema, normalizedIso } from '@/currency/api/types';
import {
  sparseCustomerSchema,
  sparseItemFieldSchema,
  sparseItemSchema,
  sparseLocationWithParentSchema,
  sparsePersonSchema,
  sparseProjectSchema,
  sparseShipmentSchema,
} from '@/sparse/api/types';
import {
  parseAndFormatDate,
  parseAndFormatDateTime,
  parseAndFormatTime,
} from '@/utilities/date-time';
import { dateSchema, dateTimeSchema } from '@/utilities/date-time-schema';
import { DateTime } from 'luxon';
import { z } from 'zod';

/**
 * Schema for asset ownership events that track changes in asset possession.
 * Each event represents a transfer of ownership or custody of an asset.
 */
export const assetOwnershipEventSchema = z.object({
  id: z.string(),
  key: z.string(),
  date: dateSchema,
  customer: sparseCustomerSchema.nullable(),
  salePrice: currencySchema.nullable(),
  createdAt: dateTimeSchema,
  lastModifiedAt: dateTimeSchema,
});

export type AssetOwnershipEvent = z.infer<typeof assetOwnershipEventSchema>;

/**
 * Schema defining different types of events that can occur in an asset's lifecycle.
 * Events can be standalone or part of an event group (e.g., shipping events).
 */
const eventTypeSchema = z.object({
  id: z.string(),
  name: z.string(),
  requiresEventGroup: z.boolean().optional(),
});

/**
 * Defines the allowed property types for events.
 * TEXT: Simple text input fields
 * CARRIER_TRACKING: Specialized fields for shipping carrier and tracking information
 */
const eventPropertyTypeSchema = z.enum(['TEXT', 'CARRIER_TRACKING']);

/**
 * Schema for properties that can be attached to events.
 * Each property has a type that determines its behavior and validation rules.
 */
const eventPropertySchema = z.object({
  id: z.string(),
  type: eventPropertyTypeSchema,
  label: z.string(),
});

/**
 * Schema for event configurations that define the structure and properties of events.
 * Each configuration specifies the event type and its associated properties.
 * Note: The API returns null for empty property arrays, which are transformed to empty arrays.
 */
export const eventConfigurationSchema = z.object({
  type: eventTypeSchema,
  properties: z.array(eventPropertySchema).nullable(),
});

export type EventConfiguration = z.infer<typeof eventConfigurationSchema>;

/**
 * Schema for event group configurations that organize related events.
 * Groups can contain multiple events with their respective configurations.
 */
export const eventGroupConfigurationSchema = z.object({
  id: z.string(),
  key: z.string(),
  name: z.string(),
  events: z.array(eventConfigurationSchema),
});

export type EventGroupConfiguration = z.infer<
  typeof eventGroupConfigurationSchema
>;

/**
 * Schema for text-based event properties.
 * Used for simple string inputs in event forms.
 */
const eventPropertyTextSchema = z.object({
  id: z.string(),
  type: z.literal('TEXT'),
  label: z.string(),
  value: z.string().nullable(),
});

/**
 * Schema for carrier tracking information in shipping events.
 * Includes carrier name, type, and tracking number details.
 */
export const eventPropertyCarrierTrackingValueSchema = z.object({
  name: z.string().optional().nullable(),
  // A carrier code not (yet) in shippingCarrierSchema (e.g. a newly added Shippo
  // carrier) degrades to null rather than dropping the whole tracking property.
  carrier: shippingCarrierSchema.nullable().catch(null),
  trackingNumber: z.string().nullable(),
});

/**
 * Schema for carrier tracking event properties.
 * Used specifically for shipping-related events that require tracking information.
 */
const eventPropertyCarrierTrackingSchema = z.object({
  id: z.string(),
  type: z.literal('CARRIER_TRACKING'),
  label: z.string(),
  value: eventPropertyCarrierTrackingValueSchema.nullable(),
});

/**
 * Union schema of all possible event property data types.
 * Currently supports text and carrier tracking properties.
 */
const eventPropertyDataSchema = z.union([
  eventPropertyTextSchema,
  eventPropertyCarrierTrackingSchema,
]);

/**
 * Core schema for events in the asset management system.
 * Events represent significant points in an asset's lifecycle, such as shipping,
 * maintenance, or status changes. Each event can have properties and timing information.
 */
export const eventSchema = z.object({
  id: z.string(),
  type: eventTypeSchema,
  sequence: z.number(),
  isRequired: z.boolean(),
  createdAt: dateTimeSchema,
  occurredAt: dateTimeSchema.nullable(),
  expectedAt: dateTimeSchema.nullable(),
  promiseDate: dateSchema.nullable(),
  eventTimezone: z.string().optional(),
  properties: z.array(eventPropertyDataSchema),
});

export type Event = z.infer<typeof eventSchema>;

/**
 * Finds the earliest date among a list of events.
 * Considers both occurred and expected dates, returning null if no dates are found.
 * Used for determining the start of an event sequence.
 *
 * @param events - Array of events to analyze
 * @returns The earliest DateTime found, or null if no dates exist
 */
function getMinimumDate(events: Event[]): DateTime | null {
  if (events.length === 0) {
    return null;
  }

  return events.reduce<DateTime | null>((minimumDate, event) => {
    const eventDate = event.occurredAt ?? event.expectedAt;

    if (eventDate === null) {
      return minimumDate;
    }

    if (minimumDate === null) {
      return eventDate;
    }

    return DateTime.min(eventDate, minimumDate);
  }, null);
}

/**
 * Finds the latest date among a list of events.
 * Considers both occurred and expected dates, returning null if no dates are found.
 * Used for determining the end of an event sequence.
 *
 * @param events - Array of events to analyze
 * @returns The latest DateTime found, or null if no dates exist
 */
function getMaximumDate(events: Event[]): DateTime | null {
  if (events.length === 0) {
    return null;
  }

  return events.reduce<DateTime | null>((maximumDate, event) => {
    const eventDate = event.occurredAt ?? event.expectedAt;

    if (eventDate === null) {
      return maximumDate;
    }

    if (maximumDate === null) {
      return eventDate;
    }

    return DateTime.max(eventDate, maximumDate);
  }, null);
}

/**
 * Schema for event groups that organize related events.
 * Groups can represent processes like shipping that involve multiple steps.
 * Includes origin and destination locations, and automatically calculates
 * the minimum and maximum dates across all events in the group.
 */
export const eventGroupSchema = z
  .object({
    id: z.string(),
    key: z.string(),
    origin: sparseLocationWithParentSchema.nullable(),
    destination: sparseLocationWithParentSchema.nullable(),
    shipment: sparseShipmentSchema.nullable(),
    configuration: eventGroupConfigurationSchema,
    events: z.array(eventSchema),
  })
  .transform((group) => {
    const minimumDate = getMinimumDate(group.events);
    const maximumDate = getMaximumDate(group.events);

    return { minimumDate, maximumDate, ...group };
  });

export type EventGroup = z.infer<typeof eventGroupSchema>;

/**
 * Schema for asset ownership that tracks customer relationships.
 * Represents the current ownership state of an asset to a customer.
 */
const assetOwnershipSchema = z.object({
  ownershipChangeDate: dateSchema.nullable(),
  customer: sparseCustomerSchema.nullable(),
});

/**
 * Schema for asset accounting properties.
 * Defines financial tracking information used for depreciation calculations
 * and financial reporting.
 */
export const costAdjustmentSchema = z.object({
  key: z.string(),
  adjustmentType: costAdjustmentTypeSchema,
  effectiveDate: dateSchema,
  amount: currencySchema,
  reason: costAdjustmentReasonSchema,
  notes: z.string().nullable(),
  createdBy: sparsePersonSchema,
  createdAt: dateTimeSchema,
});

export type CostAdjustment = z.infer<typeof costAdjustmentSchema>;

export const usefulLifeRevisionSchema = z.object({
  key: z.string(),
  effectiveDate: dateSchema,
  usefulLifeMonths: z.number(),
  reason: usefulLifeRevisionReasonSchema,
  notes: z.string().nullable(),
  createdBy: sparsePersonSchema,
  createdAt: dateTimeSchema,
});

export type UsefulLifeRevision = z.infer<typeof usefulLifeRevisionSchema>;

export const assetAccountingPropertiesSchema = z.object({
  assetExceptions: z.array(exceptionTypeSchema),
  costAdjustments: z.array(costAdjustmentSchema).catch([]),
  usefulLifeRevisions: z.array(usefulLifeRevisionSchema).catch([]),
  salvageValue: currencySchema.nullable(),
  inServiceDate: dateSchema.nullable(),
  firstSaleDate: dateSchema.nullable(),
  usefulLife: z.number().nullable(),
  depreciationModel: depreciationModelSchema.nullable(),
  depreciationPeriods: z
    .array(
      z.object({
        date: dateSchema,
        bookValue: currencySchema,
        depreciation: currencySchema,
        accumulated: currencySchema,
      })
    )
    .nullable(),
  acquisitionCogs: currencySchema.nullable(),
  capitalizedCosts: currencySchema.nullable(),
  totalPotentialCOGS: currencySchema.nullable(),
  simpleCostBasis: currencySchema.nullable(),
  billOfMaterials: currencySchema.nullable(),
  directLabor: currencySchema.nullable(),
  allocatedIndirect: currencySchema.nullable(),
  freightInbound: currencySchema.nullable(),
  freightOutbound: currencySchema.nullable(),
  installation: currencySchema.nullable(),
  tax: currencySchema.nullable(),
  tariffs: currencySchema.nullable(),
  interest: currencySchema.nullable(),
  isInServiceDateManagedAutomatically: z.boolean(),
  // Required on the asset and only ever written from the app-allowed set, so the schema
  // says so: a wider type would let an anomalous code through the boundary and only fail
  // later, at the selector or on the next save.
  costCurrencyCode: normalizedIso(orgDefaultCurrencyCodeSchema),
  isCostCurrencyLocked: z.boolean(),
});

export type AssetAccountingProperties = z.infer<
  typeof assetAccountingPropertiesSchema
>;

/**
 * Schema for asset metadata fields.
 * Captures additional item-specific information through
 * customizable fields defined at the item level.
 *
 * This schema ensures that the value type matches the field type.
 * It uses a union of nullable types to allow for null values.
 * The refine method is used to validate the value type.
 */
export const assetMetadataSchema = z
  .object({
    value: z.union([
      z.string().nullable(),
      z.number().nullable(),
      z.boolean().nullable(),
    ]),
    field: sparseItemFieldSchema,
  })
  .superRefine((metadata, ctx) => {
    const value = metadata.value;
    const mdType = metadata.field.type;
    if (value === null) {
      return;
    }
    switch (mdType) {
      case 'date':
        if (typeof value === 'string' && parseAndFormatDate(value).isValid) {
          return;
        }
        ctx.addIssue({
          code: 'custom',
          message: 'Date must be in format YYYY-MM-DD or MMM D, YYYY',
        });
        return;
      case 'time':
        if (typeof value === 'string' && parseAndFormatTime(value).isValid) {
          return;
        }
        ctx.addIssue({
          code: 'custom',
          message:
            'Time must be formatted as HH:MM or HH:MM:SS, and may include optional AM or PM',
        });
        return;
      case 'date_time':
        if (
          typeof value === 'string' &&
          parseAndFormatDateTime(value).isValid
        ) {
          return;
        }
        ctx.addIssue({
          code: 'custom',
          message:
            'Datetime must be in format YYYY-MM-DD HH:MM with optional seconds and AM/PM',
        });
        return;

      case 'integer':
        if (typeof value === 'number' && Number.isInteger(value)) {
          return;
        }

        ctx.addIssue({
          code: 'custom',
          message: 'Must be a valid integer',
        });
        return;

      case 'number':
        if (
          typeof value === 'number' &&
          Number.isFinite(value) &&
          !Number.isNaN(value)
        ) {
          return;
        }
        ctx.addIssue({
          code: 'custom',
          message: 'Must be a valid decimal number',
        });
        return;
      case 'boolean':
        if (typeof value === 'boolean') {
          return;
        }
        ctx.addIssue({
          code: 'custom',
          message: 'Must be a valid true or false',
        });
        return;
    }
  });

export type AssetMetadata = z.infer<typeof assetMetadataSchema>;

/**
 * Schema for asset disposal (scrapping) information.
 * Tracks when and why an asset was taken out of service,
 * including any financial recovery through disposal sale.
 * Transforms API field names to more intuitive internal names.
 */
const assetScrapSchema = z.object({
  disposalDate: dateSchema,
  disposalPrice: currencySchema.nullable(),
  disposalReason: z.string().nullable(),
});

export const sparseAssetSchema = z.object({
  id: z.string(),
  key: z.string(),
  serial: z.string(),
  item: sparseItemSchema,
  functionalStatus: assetFunctionalStatusSchema,
  isArchived: z.boolean(),
  isInTransit: z.boolean(),
  inInventoryDate: dateSchema,
});

export type SparseAsset = z.infer<typeof sparseAssetSchema>;

const partialAssetSchema = sparseAssetSchema.extend({
  description: z.string().nullable(),
  ownership: assetOwnershipSchema.nullable(),
  scrappedContext: assetScrapSchema.nullable(),
  inInventoryDate: dateSchema,
  project: sparseProjectSchema.nullable(),
  // Only the denomination, so the sale and scrap dialogs can lock their price inputs to
  // it. The backend serializes the whole accounting block; the rest is not needed here.
  accounting: z.object({
    costCurrencyCode: normalizedIso(orgDefaultCurrencyCodeSchema),
  }),
});

export type PartialAsset = z.infer<typeof partialAssetSchema>;

/**
 * Schema definition for in-transit variant.
 *
 * This schema is used to validate the structure of an object representing
 * an entity that is currently in transit. It ensures that the required
 * properties for an in-transit state are defined and properly typed.
 *
 * Properties:
 * - `isInTransit`: A boolean literal that must be `true`, indicating the entity is in transit.
 * - `currentLocation`: An optional sparse location schema representing the current location,
 *    which can also be `null` if the current location is not available.
 * - `previousLocation`: A required sparse location schema representing the entity's last known location.
 */
const inTransitVariantSchema = z.object({
  isInTransit: z.literal(true),
  currentLocation: sparseLocationWithParentSchema.nullable(),
  previousLocation: sparseLocationWithParentSchema,
});

const notInTransitVariantSchema = z.object({
  isInTransit: z.literal(false),
  currentLocation: sparseLocationWithParentSchema,
  previousLocation: sparseLocationWithParentSchema.nullable(),
});

/**
 * Schema definition for an asset, combining multiple constraints and conditions.
 *
 * The `assetSchema` variable represents a schema constructed as an intersection of:
 * 1. `sparseAssetSchema` - A base schema with general asset properties.
 * 2. A discriminated union keyed by the `isInTransit` property. This union determines additional schema constraints based on the value of `isInTransit`, which must match one of the following variants:
 *    - `inTransitVariantSchema`: Defines the structure and requirements for assets that are currently in transit.
 *    - `notInTransitVariantSchema`: Defines the structure and requirements for assets that are not in transit.
 *
 * This schema ensures that an asset adheres to the rules of both the base `sparseAssetSchema` and one of the variants determined by the `isInTransit` discriminator.
 */
export const assetSchema = z.intersection(
  partialAssetSchema,
  z.discriminatedUnion('isInTransit', [
    inTransitVariantSchema,
    notInTransitVariantSchema,
  ])
);

/**
 * Type definition for a complete asset entity.
 * Represents all possible states and properties of an asset,
 * including its location, status, and relationships.
 */
export type Asset = z.infer<typeof assetSchema>;

export const detailedAssetSchema = z.intersection(
  assetSchema,
  z.object({
    activity: z.array(eventSchema),
    fileCount: z.number(),
    urlLinkCount: z.number(),
    ticketCount: z.number(),
    accounting: assetAccountingPropertiesSchema,
    metadata: z.array(assetMetadataSchema).catch([]),
    nextLocation: sparseLocationWithParentSchema.nullable(),
  })
);

export type DetailedAsset = z.infer<typeof detailedAssetSchema>;
